import * as ReactModule from 'react';
import * as ReactJsxRuntime from 'react/jsx-runtime';
import * as zodModule from 'zod';
import type { SiteWorkspace, SiteWorkspaceFile } from '../domains/site-workspace/schema';

export type WorkspaceRuntimeExternalModules = Record<string, unknown>;
export type WorkspaceRuntimeComponent = ReactModule.ComponentType<Record<string, unknown>>;
export type WorkspaceRuntimeValidationResult =
  | {
      ok: true;
    }
  | {
      ok: false;
      message: string;
    };

type WorkspaceRuntimeCache = {
  compiledModules: Map<string, unknown | null>;
  pendingModules: Map<string, Promise<unknown | null>>;
};

const workspaceRuntimeCacheByWorkspace = new WeakMap<SiteWorkspace, WorkspaceRuntimeCache>();
const typescriptModulePromise = import('typescript');

export function hasWorkspaceRuntimeDefaultExport(source: string) {
  return /\bexport\s+default\b/iu.test(source);
}

export function resolveWorkspaceRuntimeComponent(module: unknown): WorkspaceRuntimeComponent | null {
  if (typeof module === 'function') {
    return module as WorkspaceRuntimeComponent;
  }

  if (!module || typeof module !== 'object') {
    return null;
  }

  for (const key of ['default', 'Page', 'Route', 'PageComponent']) {
    const candidate = (module as Record<string, unknown>)[key];
    if (typeof candidate === 'function') {
      return candidate as WorkspaceRuntimeComponent;
    }
  }

  return null;
}

function getWorkspaceRuntimeCache(workspace: SiteWorkspace): WorkspaceRuntimeCache {
  const existing = workspaceRuntimeCacheByWorkspace.get(workspace);
  if (existing) {
    return existing;
  }

  const nextCache: WorkspaceRuntimeCache = {
    compiledModules: new Map(),
    pendingModules: new Map(),
  };
  workspaceRuntimeCacheByWorkspace.set(workspace, nextCache);
  return nextCache;
}

function normalizeWorkspacePath(path: string) {
  const normalizedParts: string[] = [];

  for (const segment of path.split('/')) {
    if (!segment || segment === '.') continue;
    if (segment === '..') {
      normalizedParts.pop();
      continue;
    }
    normalizedParts.push(segment);
  }

  return normalizedParts.join('/');
}

function getWorkspaceDirectory(path: string) {
  const slashIndex = path.lastIndexOf('/');
  return slashIndex >= 0 ? path.slice(0, slashIndex) : '';
}

function resolveWorkspaceImportPath(fromFilePath: string, specifier: string) {
  const normalizedSpecifier = specifier.startsWith('/')
    ? specifier.slice(1)
    : normalizeWorkspacePath(
      getWorkspaceDirectory(fromFilePath)
        ? `${getWorkspaceDirectory(fromFilePath)}/${specifier}`
        : specifier,
    );

  return normalizedSpecifier.startsWith('src/')
    ? `/${normalizedSpecifier}`
    : `/${normalizedSpecifier}`;
}

function buildWorkspaceModuleCandidates(specifier: string, fromFilePath: string) {
  const resolvedSpecifier = specifier.startsWith('.')
    ? resolveWorkspaceImportPath(fromFilePath, specifier)
    : specifier.startsWith('/')
      ? specifier
      : `/${specifier}`;

  return [
    resolvedSpecifier,
    `${resolvedSpecifier}.ts`,
    `${resolvedSpecifier}.tsx`,
    `${resolvedSpecifier}.json`,
    `${resolvedSpecifier}/index.ts`,
    `${resolvedSpecifier}/index.tsx`,
    `${resolvedSpecifier}/index.json`,
  ];
}

function buildWorkspaceFileLookup(workspace: SiteWorkspace) {
  return new Map(workspace.files.map((file) => [file.path, file] as const));
}

function resolveWorkspaceRuntimeFile(
  workspaceFileLookup: ReadonlyMap<string, SiteWorkspaceFile>,
  specifier: string,
  fromFilePath: string,
) {
  return buildWorkspaceModuleCandidates(specifier, fromFilePath)
    .map((candidate) => candidate.replace(/^\/+/u, ''))
    .map((candidate) => workspaceFileLookup.get(candidate) ?? null)
    .find((candidate) => Boolean(candidate))
    ?? null;
}

function resolveExternalRuntimeModule(
  externalModules: WorkspaceRuntimeExternalModules,
  specifier: string,
  fromFilePath: string,
) {
  const candidates = specifier.startsWith('.')
    ? buildWorkspaceModuleCandidates(specifier, fromFilePath)
    : buildWorkspaceModuleCandidates(specifier, fromFilePath);

  return candidates
    .map((candidate) => externalModules[candidate])
    .find((candidate) => Boolean(candidate))
    ?? null;
}

function rewriteWorkspaceSourceImports(source: string, virtualPath: string) {
  return source.replace(
    /((?:import|export)\s+(?:type\s+)?(?:[^'"]*?\s+from\s+)?["'])([^"']+)(["'])/gu,
    (full, prefix: string, specifier: string, suffix: string) => {
      if (specifier.startsWith('.')) {
        return `${prefix}${resolveWorkspaceImportPath(virtualPath, specifier)}${suffix}`;
      }

      return full;
    },
  );
}

function parseWorkspaceJsonModule(file: SiteWorkspaceFile) {
  try {
    return JSON.parse(file.content) as unknown;
  } catch (error) {
    throw new Error(
      `Invalid workspace JSON module: ${file.path}: ${
        error instanceof Error ? error.message : String(error)
      }`,
    );
  }
}

function resolveWorkspaceRuntimeDependency(
  ts: typeof import('typescript'),
  workspace: SiteWorkspace,
  workspaceFileLookup: ReadonlyMap<string, SiteWorkspaceFile>,
  externalModules: WorkspaceRuntimeExternalModules,
  compiledModules: Map<string, unknown | null>,
  evaluatingModules: Set<string>,
  specifier: string,
  fromFilePath: string,
) {
  if (specifier === 'react') {
    return ReactModule;
  }

  if (specifier === 'react/jsx-runtime') {
    return ReactJsxRuntime;
  }

  if (specifier === 'zod') {
    return zodModule;
  }

  const workspaceFile = resolveWorkspaceRuntimeFile(workspaceFileLookup, specifier, fromFilePath);
  if (workspaceFile) {
    return compileWorkspaceRuntimeModuleSync(
      ts,
      workspace,
      workspaceFileLookup,
      externalModules,
      compiledModules,
      evaluatingModules,
      workspaceFile.path,
    );
  }

  const externalModule = resolveExternalRuntimeModule(externalModules, specifier, fromFilePath);
  if (externalModule) {
    return externalModule;
  }

  throw new Error(`Unsupported workspace runtime dependency: ${specifier}`);
}

function compileWorkspaceRuntimeModuleSync(
  ts: typeof import('typescript'),
  workspace: SiteWorkspace,
  workspaceFileLookup: ReadonlyMap<string, SiteWorkspaceFile>,
  externalModules: WorkspaceRuntimeExternalModules,
  compiledModules: Map<string, unknown | null>,
  evaluatingModules: Set<string>,
  filePath: string,
): unknown | null {
  if (compiledModules.has(filePath)) {
    return compiledModules.get(filePath) ?? null;
  }

  const file = workspaceFileLookup.get(filePath);
  if (!file) {
    return null;
  }

  if (evaluatingModules.has(filePath)) {
    throw new Error(`Circular workspace runtime dependency: ${filePath}`);
  }

  if (file.language === 'json') {
    const jsonModule = parseWorkspaceJsonModule(file);
    compiledModules.set(filePath, jsonModule);
    return jsonModule;
  }

  const transpiled = ts.transpileModule(rewriteWorkspaceSourceImports(file.content, file.path), {
    compilerOptions: {
      jsx: ts.JsxEmit.ReactJSX,
      module: ts.ModuleKind.CommonJS,
      target: ts.ScriptTarget.ES2022,
      esModuleInterop: true,
    },
    fileName: file.path,
  });

  const module = { exports: {} as Record<string, unknown> };
  evaluatingModules.add(filePath);

  try {
    const evaluator = new Function('require', 'module', 'exports', transpiled.outputText);
    evaluator((specifier: string) => resolveWorkspaceRuntimeDependency(
      ts,
      workspace,
      workspaceFileLookup,
      externalModules,
      compiledModules,
      evaluatingModules,
      specifier,
      file.path,
    ), module, module.exports);
  } finally {
    evaluatingModules.delete(filePath);
  }

  const resolvedModule = module.exports.default ?? module.exports;
  compiledModules.set(filePath, resolvedModule);
  return resolvedModule ?? null;
}

export async function loadWorkspaceRuntimeModule<T>(
  workspace: SiteWorkspace,
  filePath: string,
  externalModules: WorkspaceRuntimeExternalModules = {},
): Promise<T | null> {
  const workspaceFileLookup = buildWorkspaceFileLookup(workspace);
  const resolvedFile = resolveWorkspaceRuntimeFile(workspaceFileLookup, filePath, filePath);

  if (!resolvedFile) {
    return null;
  }

  const cache = getWorkspaceRuntimeCache(workspace);
  const cachedCompiledModule = cache.compiledModules.get(resolvedFile.path);
  if (typeof cachedCompiledModule !== 'undefined') {
    return cachedCompiledModule as T | null;
  }

  const cachedPendingModule = cache.pendingModules.get(resolvedFile.path);
  if (cachedPendingModule) {
    return cachedPendingModule as Promise<T | null>;
  }

  const nextPendingModule = typescriptModulePromise
    .then((ts) => compileWorkspaceRuntimeModuleSync(
      ts,
      workspace,
      workspaceFileLookup,
      externalModules,
      cache.compiledModules,
      new Set<string>(),
      resolvedFile.path,
    ))
    .catch((error) => {
      console.warn('site-workspace-runtime:compile-failed', {
        filePath: resolvedFile.path,
        message: error instanceof Error ? error.message : String(error),
      });
      return null;
    })
    .finally(() => {
      cache.pendingModules.delete(resolvedFile.path);
    });

  cache.pendingModules.set(resolvedFile.path, nextPendingModule);
  return nextPendingModule as Promise<T | null>;
}

export async function validateWorkspaceRuntimeModule(
  workspace: SiteWorkspace,
  filePath: string,
  externalModules: WorkspaceRuntimeExternalModules = {},
): Promise<WorkspaceRuntimeValidationResult> {
  const workspaceFileLookup = buildWorkspaceFileLookup(workspace);
  const resolvedFile = resolveWorkspaceRuntimeFile(workspaceFileLookup, filePath, filePath);

  if (!resolvedFile) {
    return {
      ok: false,
      message: `${filePath} が workspace 内で見つかりません。`,
    };
  }

  try {
    const ts = await typescriptModulePromise;
    const module = compileWorkspaceRuntimeModuleSync(
      ts,
      workspace,
      workspaceFileLookup,
      externalModules,
      new Map<string, unknown | null>(),
      new Set<string>(),
      resolvedFile.path,
    );

    if (module === null) {
      return {
        ok: false,
        message: `${resolvedFile.path} の runtime module を解決できませんでした。`,
      };
    }

    return {
      ok: true,
    };
  } catch (error) {
    return {
      ok: false,
      message: error instanceof Error ? error.message : String(error),
    };
  }
}
